import { useEffect, useRef, useState } from "react";
import { AiOutlineFileText, AiOutlineMail, AiOutlineMessage, AiOutlinePhone, AiOutlineUser } from "react-icons/ai";
import contactUsBgImg from "../../assets/contactusbg.png";
import ownfoodFooterImg from "../../assets/ownfoodfooter.png";
import PageHeader from "../../features/shared/PageHeader";
import { ErrorToast, IsEmail, IsEmpty, SuccessToast } from "../../Helper/FormHelper";
import styles from "./styles/ContactUs.module.css";

/** Published in the privacy policy and governing-law pages too. */
export const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "support@ownfood.com";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    subject: "",
    message: "",
  });

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("880");
  const [mobileError, setMobileError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [focusedField, setFocusedField] = useState("");

  const formRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const mobileRef = useRef(null);
  const successTimerRef = useRef(null);

  useEffect(() => {
    const baseUrl = (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.BASE_URL)
      ? import.meta.env.BASE_URL
      : "/";

    fetch(`${baseUrl}country.json`)
      .then((response) => response.json())
      .then((data) => {
        setCountries(Array.isArray(data) ? data : []);
        const bangladesh = Array.isArray(data) ? data.find((c) => c.iso2 === "BD") : null;
        if (bangladesh?.phone_code) {
          setSelectedCountry(String(bangladesh.phone_code));
        }
      })
      .catch(() => {
        setCountries([]);
        setSelectedCountry("880");
      });

    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors on change
    if (name === 'email') {
      setEmailError("");
    }
    if (name === 'mobile') {
      setMobileError("");
    }
    if (name === 'fullName') {
      setNameError("");
    }
    if (name === 'message') {
      setMessageCount(value.length);
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    setFormData((prev) => ({
      ...prev,
      mobile: numericValue,
    }));
    setMobileError("");
  };

  const validateMobile = () => {
    const { mobile } = formData;

    if (selectedCountry === '880') {
      // Bangladesh mobile validation
      const mobileRegex = /^01[3-9][0-9]{8}$/;
      if (mobile.length === 11) {
        if (!mobileRegex.test(mobile)) {
          setMobileError('Invalid Bangladeshi mobile number!');
          return false;
        }
      } else {
        setMobileError('Mobile number must be 11 digits');
        return false;
      }
    } else {
      // Generic validation for other countries
      if (mobile.length < 7 || mobile.length > 15) {
        setMobileError('Invalid mobile number');
        return false;
      }
    }

    setMobileError("");
    return true;
  };

  const validateEmail = () => {
    const { email } = formData;
    if (!IsEmail(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError("");
    return true;
  };

  const validateName = () => {
    const { fullName } = formData;
    const nameRegex = /^[\p{L}][\p{L}\s.'-]*$/u;

    if (fullName.trim().length < 2) {
      setNameError('Name must be at least 2 characters');
      return false;
    }

    if (!nameRegex.test(fullName)) {
      setNameError('Name cannot contain numbers or special characters');
      return false;
    }

    setNameError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { fullName, mobile, subject, message } = formData;

    // Validation with smooth scrolling to errors
    if (IsEmpty(fullName)) {
      ErrorToast("Name is required!");
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameRef.current?.focus();
      return;
    }

    if (!validateName()) {
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nameRef.current?.focus();
      return;
    }

    if (!validateEmail()) {
      emailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emailRef.current?.focus();
      return;
    }

    if (IsEmpty(mobile)) {
      setMobileError("Mobile number is required");
      mobileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      mobileRef.current?.focus();
      return;
    }

    if (!validateMobile()) {
      mobileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      mobileRef.current?.focus();
      return;
    }

    if (IsEmpty(subject)) {
      ErrorToast("Subject is required!");
      return;
    }

    if (IsEmpty(message)) {
      ErrorToast("Message is required!");
      return;
    }

    if (message.length < 10) {
      ErrorToast("Message must be at least 10 characters!");
      return;
    }

    setIsSubmitting(true);

    try {
      // The API has no contact/support module, so there is nowhere to POST
      // this. Rather than claim it was sent, the message is handed to the
      // visitor's own mail client addressed to support — which does deliver it.
      const body = [
        `Name: ${fullName}`,
        `Email: ${formData.email}`,
        `Phone: +${selectedCountry}${mobile}`,
        "",
        message,
      ].join("\n");

      window.location.href =
        `mailto:${SUPPORT_EMAIL}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      setShowSuccess(true);
      SuccessToast("Your email app is opening — send the message to reach us.");

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        mobile: "",
        subject: "",
        message: "",
      });
      setMessageCount(0);

      // Scroll to top of form
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Hide success message after 5 seconds
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      successTimerRef.current = setTimeout(() => {
        setShowSuccess(false);
      }, 5000);

    } catch (error) {
      ErrorToast("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.contactUsSection}>
      <div className="container page-navbar-space">
        <PageHeader>Contact Us</PageHeader>
        {/* Main Content */}
        <div className={styles.map_container} style={{ marginTop: "50px" }}>
          {/* Left Section */}
          <div className={styles.rightColumn} ref={formRef}>
            <h3>Let’s Get Together</h3>
            <p className={styles.formSubtitle}>
              Fill this in and we'll open your email app with the message ready
              to send to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              A member of our team will be in touch soon.
            </p>

            {/* Success Message */}
            {showSuccess && (
              <div style={{
                padding: "16px",
                backgroundColor: "#d4edda",
                border: "1px solid #c3e6cb",
                borderRadius: "6px",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                animation: "slideDown 0.3s ease-out"
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#28a745">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <div>
                  <strong style={{ color: "#155724" }}>Almost there!</strong>
                  <p style={{ margin: "4px 0 0", color: "#155724", fontSize: "14px" }}>
                    We've opened your email app with the message ready. Press send
                    and we'll get back to you. Nothing happened? Write to{" "}
                    <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#155724", fontWeight: 700 }}>
                      {SUPPORT_EMAIL}
                    </a>.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Full Name */}
              <div className={styles.formRow}>
                <label htmlFor="fullName">Your Full Name <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ position: "relative" }}>
                  <input
                    ref={nameRef}
                    type="text"
                    id="fullName"
                    name="fullName"
                    placeholder="Enter your name"
                    value={formData.fullName}
                    onChange={handleChange}
                    onBlur={() => {
                      setFocusedField("");
                      validateName();
                    }}
                    onFocus={() => setFocusedField('fullName')}
                    style={{
                      paddingLeft: "40px",
                      borderColor: nameError ? "#dc3545" : focusedField === 'fullName' ? "#1b6dc1" : "#dcdcdc",
                      transition: "border-color 0.3s ease"
                    }}
                    required
                  />
                  <AiOutlineUser style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: focusedField === 'fullName' ? "#1b6dc1" : "#999",
                    fontSize: "18px",
                    transition: "color 0.3s ease"
                  }} />
                </div>
                {nameError && (
                  <span style={{ color: "#dc3545", fontSize: "13px", marginTop: "4px", display: "block" }}>
                    {nameError}
                  </span>
                )}
              </div>

              {/* Email */}
              <div className={styles.formRow}>
                <label htmlFor="email">Email Address <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ position: "relative" }}>
                  <input
                    ref={emailRef}
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => {
                      setFocusedField("");
                      validateEmail();
                    }}
                    onFocus={() => setFocusedField('email')}
                    style={{
                      paddingLeft: "40px",
                      borderColor: emailError ? "#dc3545" : focusedField === 'email' ? "#1b6dc1" : "#dcdcdc",
                      transition: "border-color 0.3s ease"
                    }}
                    required
                  />
                  <AiOutlineMail style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: focusedField === 'email' ? "#1b6dc1" : "#999",
                    fontSize: "18px",
                    transition: "color 0.3s ease"
                  }} />
                </div>
                {emailError && (
                  <span style={{ color: "#dc3545", fontSize: "13px", marginTop: "4px", display: "block" }}>
                    {emailError}
                  </span>
                )}
              </div>

              {/* Mobile Number */}
              <div className={styles.formRow}>
                <label htmlFor="mobile">Mobile Number <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ display: "flex", gap: "8px", position: "relative" }}>
                  <div style={{ flex: 1, position: "relative" }}>
                    <input
                      ref={mobileRef}
                      type="text"
                      id="mobile"
                      name="mobile"
                      placeholder={selectedCountry === '880' ? '01XXXXXXXXX' : 'Enter mobile number'}
                      value={formData.mobile}
                      onChange={handleMobileChange}
                      onBlur={() => {
                        setFocusedField("");
                        validateMobile();
                      }}
                      onFocus={() => setFocusedField('mobile')}
                      maxLength="15"
                      style={{
                        width: "100%",
                        paddingLeft: "40px",
                        borderColor: mobileError ? "#dc3545" : focusedField === 'mobile' ? "#1b6dc1" : "#dcdcdc",
                        transition: "border-color 0.3s ease"
                      }}
                      required
                    />
                    <AiOutlinePhone style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: focusedField === 'mobile' ? "#1b6dc1" : "#999",
                      fontSize: "18px",
                      transition: "color 0.3s ease"
                    }} />
                  </div>
                </div>
                {mobileError && (
                  <span style={{ color: "#dc3545", fontSize: "13px", marginTop: "4px", display: "block" }}>
                    {mobileError}
                  </span>
                )}
              </div>

              {/* Subject */}
              <div className={styles.formRow}>
                <label htmlFor="subject">Subject <span style={{ color: "#dc3545" }}>*</span></label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    placeholder="Enter Your Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('subject')}
                    onBlur={() => setFocusedField("")}
                    style={{
                      paddingLeft: "40px",
                      borderColor: focusedField === 'subject' ? "#1b6dc1" : "#dcdcdc",
                      transition: "border-color 0.3s ease"
                    }}
                    required
                  />
                  <AiOutlineFileText style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: focusedField === 'subject' ? "#1b6dc1" : "#999",
                    fontSize: "18px",
                    transition: "color 0.3s ease"
                  }} />
                </div>
              </div>

              {/* Message */}
              <div className={styles.formRow}>
                <label htmlFor="message">
                  Message <span style={{ color: "#dc3545" }}>*</span>
                  <span style={{ float: "right", fontSize: "13px", color: "#999", fontWeight: "400" }}>
                    {messageCount}/500 {messageCount < 10 && messageCount > 0 && <span style={{ color: "#dc3545" }}>(min 10)</span>}
                  </span>
                </label>
                <div style={{ position: "relative" }}>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Enter details..."
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField("")}
                    rows="5"
                    maxLength="500"
                    style={{
                      paddingLeft: "40px",
                      paddingTop: "12px",
                      borderColor: focusedField === 'message' ? "#1b6dc1" : "#dcdcdc",
                      transition: "border-color 0.3s ease",
                      resize: "vertical"
                    }}
                    required
                  />
                  <AiOutlineMessage style={{
                    position: "absolute",
                    left: "12px",
                    top: "14px",
                    color: focusedField === 'message' ? "#1b6dc1" : "#999",
                    fontSize: "18px",
                    transition: "color 0.3s ease"
                  }} />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={isSubmitting}
                style={{
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.3s ease"
                }}
              >
                {isSubmitting && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ animation: "spin 1s linear infinite" }}
                  >
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" opacity="0.25" />
                    <path
                      d="M12 2a10 10 0 0 1 10 10"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                {isSubmitting ? 'Sending Message...' : 'Submit Message'}
              </button>
            </form>
          </div>

          {/* Right Section */}
          <div className="py-5 py-md-0">
            <h2 className={styles.map_title}>
              <strong style={{ color: "#1B6DC1" }}>#</strong>Contactless delivery available
            </h2>

            {/* Icon */}
            <div
              style={{
                margin: "40px 0",
                height: "300px",
                backgroundImage: `url(${contactUsBgImg})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "cover",
              }}
              className="d-flex align-items-center justify-content-center rounded-1"
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#1B6DC1",
                  borderRadius: "50% 50% 50% 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: "rotate(-45deg)",
                }}
              >
                <img src={ownfoodFooterImg} alt="Ownfood Footer" style={{ transform: "rotate(45deg)" }} width={60} height={45} />
              </div>
            </div>

            {/* Headquarters */}
            <div className={styles.map_address}>
              {/* Headquarter - USA */}
              <div>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#2D3643",
                    marginBottom: "10px",
                  }}
                >
                  Headquarter
                </h4>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#3D4A5C",
                    lineHeight: "1.6",
                  }}
                >
                  29135 Ryan Rd,
                  <br />
                  Suite D. Warren MI 48092
                </p>
              </div>

              {/* Dhaka Office */}
              <div>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#2D3643",
                    marginBottom: "10px",
                  }}
                >
                  Dhaka Office
                </h4>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#3D4A5C",
                    lineHeight: "1.6",
                  }}
                >
                  Jahir Smart Tower
                  <br />
                  Level 4
                  <br />
                  205/1/A West Kafrul
                  <br />
                  Begum Rokeya Sharani
                  <br />
                  Dhaka 1207
                </p>
              </div>

              {/* Corporate Office */}
              <div>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#2D3643",
                    marginBottom: "10px",
                  }}
                >
                  Corporate Office
                </h4>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#3D4A5C",
                    lineHeight: "1.6",
                  }}
                >
                  1534 Lake View R/A
                  <br />
                  Foys Lake
                  <br />
                  Khulshi
                  <br />
                  Chattogram 4201
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
